// ============================================================
// ORDERS PAGE — Simplified orders with tabs
// Route: /admin/orders
// Tabs: Live Orders, Completed, Cancelled, Rejected
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { ShoppingBag, Eye, RefreshCw, Download, Clock, CheckCircle, XCircle, Ban } from 'lucide-react';
import {
  Badge, Button, StatCard, SearchInput, Select, Pagination, Modal,
} from '../components/ui';
import { usePagination, useSearch } from '../hooks';
import { adminApi } from '../services/apiService';
import { getOrders } from '../services/mockData';
import { formatCurrency, formatDateTime, formatDate, downloadCSV } from '../utils/helpers';
import { ORDER_STATUS_LABELS, ORDER_STATUS_COLORS } from '../utils/constants';
import { toast } from 'react-toastify';

const Spinner = () => (
  <div style={{ textAlign: 'center', padding: 60, color: 'var(--admin-text-muted)' }}>
    <div style={{ width: 36, height: 36, border: '3px solid rgba(232,93,4,0.3)', borderTopColor: '#E85D04', borderRadius: '50%', animation: 'admin-spin 0.8s linear infinite', margin: '0 auto 12px' }} />
    <p>Loading orders…</p>
  </div>
);

const Empty = ({ title }) => (
  <div style={{ textAlign: 'center', padding: 60, color: 'var(--admin-text-muted)' }}>
    <ShoppingBag size={44} style={{ opacity: 0.2, marginBottom: 12 }} />
    <p style={{ fontWeight: 700 }}>{title}</p>
  </div>
);

const StatusBadge = ({ status }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px',
    borderRadius: 20, fontSize: 11, fontWeight: 700,
    background: (ORDER_STATUS_COLORS[status] || '#94A3B8') + '22',
    color: ORDER_STATUS_COLORS[status] || '#94A3B8',
    border: `1px solid ${(ORDER_STATUS_COLORS[status] || '#94A3B8')}44`,
  }}>
    {ORDER_STATUS_LABELS[status] || status}
  </span>
);

const VALID_TRANSITIONS = {
  new:       ['accepted', 'rejected'],
  accepted:  ['preparing', 'cancelled'],
  preparing: ['ready', 'cancelled'],
  ready:     ['completed'],
  completed: [], cancelled: [], rejected: [],
};

// ── Order Row ────────────────────────────────────────────────
const OrderTable = ({ orders, onView, onStatusChange, updatingId }) => {
  if (orders.length === 0) return <Empty title="No orders found" />;

  return (
    <div style={{ borderRadius: 10, border: '1px solid var(--admin-border)', overflow: 'hidden' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: 'var(--admin-bg)' }}>
            {['Token', 'Customer', 'Restaurant', 'Items', 'Total', 'Payment', 'Status', 'Time', 'Actions'].map(h => (
              <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 700, color: 'var(--admin-text-secondary)', borderBottom: '1px solid var(--admin-border)', whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {orders.map((o, i) => {
            const transitions = VALID_TRANSITIONS[o.status] || [];
            return (
              <tr key={o.id || i} style={{ borderBottom: '1px solid var(--admin-border)' }}>
                <td style={{ padding: '12px 14px' }}>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, background: 'rgba(232,93,4,0.1)', color: '#E85D04', padding: '2px 8px', borderRadius: 6, fontSize: 12 }}>
                    #{o.pickupToken || o.pickup_token || '—'}
                  </span>
                </td>
                <td style={{ padding: '12px 14px' }}>
                  <div style={{ fontWeight: 600 }}>{o.customerName || o.customer_name || '—'}</div>
                  <div style={{ fontSize: 11, color: 'var(--admin-text-muted)' }}>{o.customerPhone || o.customer_phone || ''}</div>
                </td>
                <td style={{ padding: '12px 14px', fontWeight: 600 }}>{o.restaurantName || o.restaurant_name || '—'}</td>
                <td style={{ padding: '12px 14px', color: 'var(--admin-text-muted)', maxWidth: 160 }}>
                  {o.itemsSummary
                    ? <span style={{ fontSize: 12 }}>{o.itemsSummary}</span>
                    : (o.items || []).length > 0
                      ? <span>{(o.items || []).slice(0, 2).map(it => it.name || it.item_name).join(', ')}{(o.items || []).length > 2 ? ` +${(o.items || []).length - 2}` : ''}</span>
                      : o.itemCount
                        ? <span>{o.itemCount} item{o.itemCount > 1 ? 's' : ''}</span>
                        : '—'}
                </td>
                <td style={{ padding: '12px 14px', fontWeight: 700, color: '#10B981' }}>{formatCurrency(o.total || 0)}</td>
                <td style={{ padding: '12px 14px', fontSize: 11, textTransform: 'capitalize', color: 'var(--admin-text-muted)' }}>
                  {(o.paymentMethod || '—').replace(/_/g, ' ')}
                </td>
                <td style={{ padding: '12px 14px' }}><StatusBadge status={o.status} /></td>
                <td style={{ padding: '12px 14px', fontSize: 11, color: 'var(--admin-text-muted)', whiteSpace: 'nowrap' }}>
                  {o.createdAt ? (formatDateTime ? formatDateTime(o.createdAt) : formatDate(o.createdAt)) : 'N/A'}
                </td>
                <td style={{ padding: '12px 14px' }}>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap' }}>
                    <Button variant="info" size="sm" icon={<Eye size={12} />} onClick={() => onView(o)}>View</Button>
                    {transitions.length > 0 && transitions.slice(0, 1).map(t => (
                      <Button key={t} variant={t === 'accepted' ? 'success' : t === 'rejected' || t === 'cancelled' ? 'danger' : 'secondary'}
                        size="sm" disabled={updatingId === o.id}
                        onClick={() => onStatusChange(o, t)}>
                        {updatingId === o.id ? '…' : t.charAt(0).toUpperCase() + t.slice(1)}
                      </Button>
                    ))}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ── Order Detail Modal ────────────────────────────────────────
const OrderDetailModal = ({ order, onClose, onStatusChange, updatingId }) => {
  if (!order) return null;
  const transitions = VALID_TRANSITIONS[order.status] || [];

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={onClose}>
      <div style={{ background: 'var(--admin-surface)', borderRadius: 16, width: '100%', maxWidth: 580, maxHeight: '85vh', overflowY: 'auto', padding: 28, boxShadow: '0 25px 60px rgba(0,0,0,0.5)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontWeight: 800, fontSize: 18 }}>Order Details</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--admin-text-muted)', fontSize: 22, lineHeight: 1 }}>✕</button>
        </div>

        {/* Token + Status */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' }}>
          {order.pickupToken && (
            <span style={{ fontFamily: 'monospace', fontWeight: 800, background: 'rgba(232,93,4,0.12)', color: '#E85D04', padding: '5px 14px', borderRadius: 8, fontSize: 14, border: '1px solid rgba(232,93,4,0.25)' }}>
              #{order.pickupToken}
            </span>
          )}
          <StatusBadge status={order.status} />
        </div>

        {/* Details grid */}
        <div style={{ display: 'grid', gap: '6px 0' }}>
          {[
            ['Order ID', <code style={{ fontSize: 11, wordBreak: 'break-all' }}>{order.id}</code>],
            ['Customer', order.customerName],
            order.customerPhone && ['Phone', order.customerPhone],
            ['Restaurant', order.restaurantName],
            ['Order Total', <b style={{ color: '#10B981' }}>{formatCurrency(order.total || 0)}</b>],
            ['Payment Method', <span style={{ textTransform: 'capitalize' }}>{(order.paymentMethod || '—')}</span>],
            ['Payment Status', <StatusBadge status={order.paymentStatus || 'pending'} />],
            ['Placed At', order.createdAt ? (formatDateTime ? formatDateTime(order.createdAt) : formatDate(order.createdAt)) : 'N/A'],
            order.note && ['Customer Note', <em style={{ color: 'var(--admin-text-muted)' }}>{order.note}</em>],
          ].filter(Boolean).map(([label, val], i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderBottom: '1px solid var(--admin-border)' }}>
              <span style={{ color: 'var(--admin-text-muted)', fontSize: 13 }}>{label}</span>
              <span style={{ fontWeight: 600, fontSize: 13 }}>{val}</span>
            </div>
          ))}
        </div>

        {/* Items */}
        <div style={{ marginTop: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: 'var(--admin-text-secondary)' }}>Items Ordered</div>
          {(order.items || []).length > 0 ? (
            (order.items || []).map((item, i) => {
              const cookingReq = (item.cooking_request || item.special_instructions || '').trim();
              return (
              <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid var(--admin-border)', fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{item.name || item.item_name} <span style={{ color: 'var(--admin-text-muted)' }}>× {item.quantity || 1}</span></div>
                    {item.addons && item.addons.length > 0 && (
                      <div style={{ fontSize: 11, color: 'var(--admin-text-muted)' }}>+ {item.addons.map(a => a.name).join(', ')}</div>
                    )}
                  </div>
                  <div style={{ color: '#10B981', fontWeight: 700 }}>{formatCurrency((item.price || 0) * (item.quantity || 1))}</div>
                </div>
                {cookingReq ? (
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 5, marginTop: 4, background: '#FFF8F5', borderLeft: '3px solid #FF6B35', borderRadius: 5, padding: '4px 8px' }}>
                    <span style={{ fontSize: 12 }}>🍳</span>
                    <span style={{ fontSize: 12, color: '#CC5500', fontStyle: 'italic', lineHeight: 1.5 }}>{cookingReq}</span>
                  </div>
                ) : null}
              </div>
              );
            })
          ) : order.itemsSummary ? (
            <div style={{ background: 'var(--admin-bg)', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: 'var(--admin-text-primary)', fontWeight: 500 }}>
              {order.itemsSummary}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--admin-text-muted)' }}>No item details available</div>
          )}
        </div>

        {/* Action Buttons */}
        {transitions.length > 0 && (
          <div style={{ display: 'flex', gap: 8, marginTop: 24 }}>
            {transitions.map(t => (
              <Button key={t}
                variant={t === 'accepted' || t === 'completed' ? 'success' : t === 'rejected' || t === 'cancelled' ? 'danger' : 'secondary'}
                disabled={updatingId === order.id}
                onClick={() => onStatusChange(order, t)}
              >
                {updatingId === order.id ? 'Updating…' : `Mark ${t.charAt(0).toUpperCase() + t.slice(1)}`}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

// ── Tab Content ───────────────────────────────────────────────
const OrdersTab = ({ statusGroup, allOrders, loading, onView, onStatusChange, updatingId }) => {
  const { query, setQuery, filtered } = useSearch(allOrders, ['id', 'customerName', 'customer_name', 'restaurantName', 'restaurant_name', 'pickupToken', 'pickup_token']);
  const { currentPage, totalPages, paginated, goTo } = usePagination(filtered);

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <SearchInput value={query} onChange={setQuery} placeholder={`Search ${statusGroup} orders…`} />
        <Button variant="secondary" icon={<Download size={13} />} onClick={() => downloadCSV(allOrders, `orders-${statusGroup}`)}>Export</Button>
        <Button variant="secondary" icon={<RefreshCw size={13} />} onClick={() => window.location.reload()}>Refresh</Button>
      </div>
      {loading ? <Spinner /> : (
        <>
          <OrderTable orders={paginated} onView={onView} onStatusChange={onStatusChange} updatingId={updatingId} />
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={filtered.length} itemsPerPage={10} onPageChange={goTo} />
        </>
      )}
    </div>
  );
};

// ── Main Orders Page ──────────────────────────────────────────
const Orders = () => {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('live');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  const normalizeOrder = (o) => ({
    ...o,
    // Token
    pickupToken: o.pickupToken || o.pickup_token || o.token || '',
    // Customer
    customerName: o.customerName || o.customer_name || o.customer || '—',
    customerPhone: o.customerPhone || o.customer_phone || '',
    // Restaurant
    restaurantName: o.restaurantName || o.restaurant_name || o.restaurant || '—',
    // Items — API returns a count + itemsSummary string, not an array
    itemsSummary: (o.itemsSummary && o.itemsSummary !== 'No items') ? o.itemsSummary : ((o.items_summary && o.items_summary !== 'No items') ? o.items_summary : ''),
    itemCount: typeof o.items === 'number' ? o.items : (Array.isArray(o.items) ? o.items.length : 0),
    items: Array.isArray(o.items) ? o.items : [],
    // Totals
    total: o.total || o.totalAmount || o.total_amount || o.amount || 0,
    // Payment
    paymentMethod: o.paymentMethod || o.payment_method || '—',
    paymentStatus: o.paymentStatus || o.payment_status || 'pending',
    // Time
    createdAt: o.createdAt || o.created_at || o.placedAt || o.placed_at || '',
  });

  const load = useCallback(() => {
    setLoading(true);
    adminApi.getOrders({ per_page: 500 }, getOrders)
      .then(res => {
        const arr = Array.isArray(res) ? res : (res?.items || res?.orders || []);
        setAll(arr.map(normalizeOrder));
        setLoading(false);
      })
      .catch(() => { setAll([]); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const liveOrders      = all.filter(o => ['new', 'accepted', 'preparing', 'ready'].includes(o.status));
  const completedOrders = all.filter(o => o.status === 'completed');
  const cancelledOrders = all.filter(o => o.status === 'cancelled');
  const rejectedOrders  = all.filter(o => o.status === 'rejected');

  const handleStatusChange = async (order, newStatus) => {
    setUpdatingId(order.id);
    try {
      await adminApi.updateOrderStatus(order.id, newStatus);
      setAll(prev => prev.map(o => o.id === order.id ? { ...o, status: newStatus } : o));
      if (selectedOrder?.id === order.id) setSelectedOrder(prev => ({ ...prev, status: newStatus }));
      toast.success(`Order marked as ${newStatus}`);
    } catch (e) {
      toast.error(e.message || 'Failed to update order');
    }
    setUpdatingId(null);
  };

  const tabs = [
    { id: 'live',      label: 'Live Orders',  count: liveOrders.length,      icon: Clock,         data: liveOrders,      color: '#F59E0B' },
    { id: 'completed', label: 'Completed',    count: completedOrders.length,  icon: CheckCircle,   data: completedOrders, color: '#10B981' },
    { id: 'cancelled', label: 'Cancelled',    count: cancelledOrders.length,  icon: Ban,           data: cancelledOrders, color: '#EF4444' },
    { id: 'rejected',  label: 'Rejected',     count: rejectedOrders.length,   icon: XCircle,       data: rejectedOrders,  color: '#DC2626' },
  ];

  const activeTabData = tabs.find(t => t.id === activeTab);

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Orders</h1>
          <p className="admin-page-subtitle">
            Manage all platform orders &nbsp;·&nbsp;
            <strong>{liveOrders.length}</strong> live
          </p>
        </div>
        <Button variant="secondary" icon={<RefreshCw size={14} />} onClick={load}>Refresh</Button>
      </div>

      {/* Stats Row */}
      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 24 }}>
        {tabs.map(tab => {
          const Icon = tab.icon;
          return <StatCard key={tab.id} label={tab.label} value={tab.count} icon={<Icon size={18} />} color={tab.color} />;
        })}
      </div>

      {/* Tab Bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--admin-border)', marginBottom: 20, overflowX: 'auto', scrollbarWidth: 'none' }}>
        {tabs.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '10px 20px', cursor: 'pointer',
              background: 'none', border: 'none',
              borderBottom: activeTab === tab.id ? '2px solid #E85D04' : '2px solid transparent',
              color: activeTab === tab.id ? '#E85D04' : 'var(--admin-text-muted)',
              fontWeight: activeTab === tab.id ? 700 : 500, fontSize: 13,
              whiteSpace: 'nowrap',
            }}>
              <Icon size={14} />
              {tab.label}
              <span style={{
                background: activeTab === tab.id ? '#E85D04' : 'var(--admin-border)',
                color: activeTab === tab.id ? '#fff' : 'var(--admin-text-muted)',
                fontSize: 10, fontWeight: 800,
                padding: '1px 6px', borderRadius: 99, marginLeft: 2,
              }}>{tab.count}</span>
            </button>
          );
        })}
      </div>

      {/* Active Tab Content */}
      {activeTabData && (
        <OrdersTab
          statusGroup={activeTab}
          allOrders={activeTabData.data}
          loading={loading}
          onView={setSelectedOrder}
          onStatusChange={handleStatusChange}
          updatingId={updatingId}
        />
      )}

      {/* Order Detail Modal */}
      <OrderDetailModal
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
        onStatusChange={handleStatusChange}
        updatingId={updatingId}
      />
    </div>
  );
};

export default Orders;
